/**
 * policy-consent.js — RC Panda Hobbies (2026-10-21 rev-2)
 *
 * 状态模型
 * ────────
 * desiredAgreed   — 用户当前明确选择，立即反映到所有复选框视觉状态。
 * confirmedAgreed — 服务器最后一次成功确认的状态。
 * saveInFlight    — 当前是否有保存请求在传输中。
 * saveFailed      — 最近一次保存是否以错误结束（且尚未重试）。
 *
 * 结账条件（全部满足才放行）：
 *   desiredAgreed === true
 *   confirmedAgreed === true
 *   saveInFlight === false
 *   saveFailed === false
 *
 * 串行写入队列
 * ────────────
 * 不使用 AbortController 取消进行中的请求。
 * 队列保存待处理的目标值（boolean）；每次 fetch 完成后检查队列，
 * 若队列中有更新的目标值则立即发起下一次保存。
 * 这保证服务器端写入顺序与用户最终选择一致。
 *
 * 错误提示
 * ────────
 * 保存失败时立即在所有实例上显示错误；不等待下次 Checkout 点击。
 * 错误提示明确告知可通过重新切换复选框重试。
 *
 * 结账拦截区域
 * ────────────
 * 按 Checkout 按钮所在的 .policy-consent 祖先（或同一 form 内最近实例）
 * 决定哪个实例显示错误和接收焦点；无法确定时回退到文档中任一可见实例。
 *
 * 平台约束
 * ────────
 * ✗ Shop Pay / Apple Pay / Google Pay 在 Payments SDK iframe 内渲染，
 *   storefront JS 无法拦截。这是平台约束，不是实现缺陷。
 */
(function () {
  'use strict';

  /* ── 常量 ─────────────────────────────────────────────────────────────── */
  var POLICY_VERSION = '2026-10-21-v1';
  var ATTR_KEY       = 'policy_consent';
  var ATTR_VAL       = 'agreed:' + POLICY_VERSION;
  var ATTR_CLEAR     = '';

  /* ── 共享页面状态 ─────────────────────────────────────────────────────── */

  /**
   * 用户当前明确选择。立即更新，用于驱动所有复选框视觉状态。
   * 初始 false（页面加载时默认未勾选，不读旧 cart.attributes）。
   */
  var desiredAgreed = false;

  /**
   * 服务器最后成功确认的状态。
   * 仅在 /cart/update 成功且 attribute 核实匹配后更新。
   */
  var confirmedAgreed = false;

  /** 当前是否有 fetch 在传输中。 */
  var saveInFlight = false;

  /**
   * 最近一次保存是否失败（且尚未通过重试恢复）。
   * 失败时立即在所有实例上显示错误提示。
   */
  var saveFailed = false;

  /**
   * 串行队列中待处理的下一个目标值。
   * null = 队列为空；boolean = 有待发送的保存请求等待当前 fetch 完成。
   * 只保留最新目标值，中间值被覆盖（用户快速切换时只保存最终状态）。
   * @type {boolean|null}
   */
  var pendingValue = null;

  /* ── 结账门控 ─────────────────────────────────────────────────────────── */

  function shouldBlock() {
    return !desiredAgreed || !confirmedAgreed || saveInFlight || saveFailed;
  }

  /* ── 实例列表 ─────────────────────────────────────────────────────────── */

  /** @type {PolicyConsent[]} */
  var instances = [];

  /** 通知所有实例同步 UI。 */
  function notifyAll() {
    instances.forEach(function (inst) { inst.sync(); });
  }

  /**
   * 失败时立即在所有实例上显示保存失败提示。
   * 不等待用户点击 Checkout。
   */
  function notifyAllSaveFailed() {
    instances.forEach(function (inst) { inst.showSaveError(); });
  }

  /* ── 串行写入队列 ─────────────────────────────────────────────────────── */

  /**
   * 调度一次保存。
   * 若当前无进行中的 fetch，立即发起；否则将目标值写入 pendingValue
   * （覆盖之前的待处理值），等当前 fetch 完成后自动发起。
   *
   * @param {boolean} agree
   */
  function scheduleSave(agree) {
    if (saveInFlight) {
      /* 有请求在途：记录最新目标值，当前请求完成后处理 */
      pendingValue = agree;
      return;
    }
    doSave(agree);
  }

  /**
   * 立即发起一次 /cart/update fetch。
   * 完成（成功或失败）后检查 pendingValue 决定是否继续发起下一次。
   *
   * @param {boolean} agree
   */
  function doSave(agree) {
    saveInFlight = true;
    saveFailed   = false;
    pendingValue = null;
    notifyAll();

    var value = agree ? ATTR_VAL : ATTR_CLEAR;

    fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [ATTR_KEY]: value } }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var saved    = cart && cart.attributes && cart.attributes[ATTR_KEY];
        var clearing = value === ATTR_CLEAR;
        var ok       = clearing
          ? (saved == null || saved === '')
          : saved === value;
        if (!ok) throw new Error('Attribute mismatch');

        /* 确认成功 */
        confirmedAgreed = agree;
        saveInFlight    = false;
        saveFailed      = false;
        notifyAll();
        drainQueue();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') {
          /* 不应发生（队列模式不取消请求），作为防御保留 */
          saveInFlight = false;
          notifyAll();
          drainQueue();
          return;
        }

        /*
         * 保存失败。
         * confirmedAgreed 不更新（保留上次已知确认状态）。
         * saveFailed=true 立即阻止结账并通知所有实例显示错误。
         * desiredAgreed 保持用户当前选择，不回滚复选框。
         */
        saveInFlight = false;
        saveFailed   = true;
        notifyAll();
        notifyAllSaveFailed();
        /*
         * 失败后仍检查队列：若用户在失败期间再次切换，
         * 排队的保存请求应继续尝试。
         */
        drainQueue();
      });
  }

  /**
   * 检查队列并在有待处理值时继续发起保存。
   * 在每次 fetch 完成（成功或失败）后调用。
   */
  function drainQueue() {
    if (pendingValue === null) return;
    var next = pendingValue;
    pendingValue = null;
    doSave(next);
  }

  /* ── PolicyConsent 实例 ───────────────────────────────────────────────── */

  /**
   * @param {HTMLElement} root     .policy-consent 容器
   * @param {string}      context  'page' | 'drawer'
   */
  function PolicyConsent(root, context) {
    this.root     = root;
    this.context  = context;
    this.checkbox = root.querySelector('.policy-consent__checkbox');
    this.errorEl  = root.querySelector('.policy-consent__error');

    this.msgRequired   = root.dataset.errorRequired ||
      'Please agree to the policies before proceeding to checkout.';
    this.msgSaveFailed = root.dataset.errorSaveFailed ||
      'Could not save your consent. Please check your connection and try again. Toggle the checkbox to retry.';

    if (!this.checkbox) return;

    this.sync();

    var self = this;
    this._onChangeBound = function () { self._onChange(); };
    this.checkbox.addEventListener('change', this._onChangeBound);
  }

  /**
   * 同步复选框视觉状态到 desiredAgreed（用户当前选择），
   * 而非 confirmedAgreed。
   * 在健康状态（saveFailed=false）时清除过时错误。
   */
  PolicyConsent.prototype.sync = function () {
    if (!this.checkbox) return;
    /* 视觉状态跟随用户选择，不跟随服务器确认状态 */
    this.checkbox.checked = desiredAgreed;
    if (!saveFailed) this._hideError();
  };

  /**
   * 用户切换复选框时：
   * 1. 立即更新 desiredAgreed 并同步所有复选框视觉状态；
   * 2. 清除此实例的错误提示；
   * 3. 重置 saveFailed（用户主动重试）；
   * 4. 调度保存。
   */
  PolicyConsent.prototype._onChange = function () {
    desiredAgreed = this.checkbox.checked;
    saveFailed    = false;   /* 用户重试，清除失败状态 */
    this._hideError();
    notifyAll();             /* 立即同步其他复选框视觉状态 */
    scheduleSave(desiredAgreed);
  };

  PolicyConsent.prototype.showRequiredError = function () {
    this._showError(this.msgRequired);
  };

  PolicyConsent.prototype.showSaveError = function () {
    this._showError(this.msgSaveFailed);
  };

  PolicyConsent.prototype.focusCheckbox = function () {
    if (this.checkbox) this.checkbox.focus();
  };

  /**
   * 此实例是否物理可见（有非零布局盒且在文档中）。
   * 用于区分"真实可见"与"隐藏在关闭抽屉中的实例"。
   * 依赖 visibility 和 display：隐藏抽屉通常有 display:none 或
   * visibility:hidden 祖先，导致 offsetParent=null 且 BCR 为零。
   * @returns {boolean}
   */
  PolicyConsent.prototype.isVisible = function () {
    if (!document.contains(this.root)) return false;
    /* offsetParent 为 null 表示 display:none 祖先（position:fixed 除外） */
    if (this.root.offsetParent === null) {
      /* 排除 position:fixed 的合法情况：检查 BCR */
      var r = this.root.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return false;
    }
    return true;
  };

  PolicyConsent.prototype._showError = function (msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.removeAttribute('hidden');
  };

  PolicyConsent.prototype._hideError = function () {
    if (!this.errorEl) return;
    this.errorEl.setAttribute('hidden', '');
    this.errorEl.textContent = '';
  };

  PolicyConsent.prototype.disconnect = function () {
    if (this.checkbox && this._onChangeBound) {
      this.checkbox.removeEventListener('change', this._onChangeBound);
    }
  };

  /* ── 按结账来源确定目标实例 ──────────────────────────────────────────── */

  /**
   * 根据触发结账的按钮，确定应显示错误和接收焦点的实例。
   *
   * 策略（按优先级）：
   * 1. 与 Checkout 按钮同属一个 cart-form 的 .policy-consent 实例，
   *    且物理可见。
   * 2. 文档中任意物理可见的实例（回退）。
   * 3. 任意实例（最终回退）。
   *
   * 不固定优先 'page' context，而是跟随实际触发事件的区域。
   *
   * @param {Element|null} triggerEl  触发结账的按钮元素
   * @returns {PolicyConsent|null}
   */
  function instanceForTrigger(triggerEl) {
    /* 策略 1：找与触发元素同一 form 容器内的可见 .policy-consent */
    if (triggerEl) {
      var form = resolveForm(triggerEl);
      if (form) {
        var inForm = instances.find(function (inst) {
          return form.contains(inst.root) && inst.isVisible();
        });
        if (inForm) return inForm;

        /*
         * cart-form 可能不包含 .policy-consent（snippet 在 form 外但同页面）。
         * 尝试找与触发按钮最近的共同祖先容器内的可见实例。
         */
        var cartArea = triggerEl.closest(
          '.cart-drawer__summary, .cart-summary__inner, [data-hydration-key="cart-drawer-inner"]'
        );
        if (cartArea) {
          var inArea = instances.find(function (inst) {
            return cartArea.contains(inst.root) && inst.isVisible();
          });
          if (inArea) return inArea;
        }
      }
    }

    /* 策略 2：任意物理可见实例 */
    var anyVisible = instances.find(function (inst) { return inst.isVisible(); });
    if (anyVisible) return anyVisible;

    /* 策略 3：任意实例 */
    return instances[0] || null;
  }

  /* ── 文档级结账拦截（只注册一次）─────────────────────────────────────── */

  var docListenersRegistered = false;

  function registerDocListeners() {
    if (docListenersRegistered) return;
    docListenersRegistered = true;
    document.addEventListener('click',  onDocClick,  { capture: true });
    document.addEventListener('submit', onDocSubmit, { capture: true });
  }

  /**
   * 解析元素的关联表单：优先 form 属性（IDref），再回退最近祖先 form。
   * @param {Element} el
   * @returns {HTMLFormElement|null}
   */
  function resolveForm(el) {
    var id = el.getAttribute('form');
    if (id) return /** @type {HTMLFormElement|null} */ (document.getElementById(id));
    return el.closest('form');
  }

  /** @param {HTMLFormElement|null} form */
  function isCartForm(form) {
    if (!form) return false;
    return form.id === 'cart-form' || form.getAttribute('action') === '/checkout';
  }

  function onDocClick(e) {
    if (!(e.target instanceof Element)) return;

    var btn = e.target.closest(
      'button[type="submit"], input[type="submit"], #checkout, [name="checkout"]'
    );
    if (!btn) return;
    if (btn.id !== 'checkout' && btn.getAttribute('name') !== 'checkout') return;
    if (!isCartForm(resolveForm(btn))) return;

    if (shouldBlock()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showBlockError(btn);
    }
  }

  function onDocSubmit(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!isCartForm(form)) return;

    var submitter  = e.submitter || null;
    var isCheckout = submitter
      ? (submitter.id === 'checkout' || submitter.getAttribute('name') === 'checkout')
      : Boolean(form.querySelector('#checkout, [name="checkout"]'));

    if (!isCheckout) return;

    if (shouldBlock()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showBlockError(submitter);
    }
  }

  /**
   * 根据触发元素选择正确实例，显示适当错误并移动焦点。
   * @param {Element|null} triggerEl
   */
  function showBlockError(triggerEl) {
    var inst = instanceForTrigger(triggerEl || null);
    if (!inst) return;
    if (saveFailed) {
      inst.showSaveError();
    } else {
      inst.showRequiredError();
    }
    inst.focusCheckbox();
  }

  /* ── 挂载 / 卸载 ──────────────────────────────────────────────────────── */

  /** @type {WeakMap<HTMLElement, PolicyConsent>} */
  var registry = new WeakMap();

  function mountAll() {
    document.querySelectorAll('.policy-consent').forEach(function (el) {
      if (!(el instanceof HTMLElement)) return;
      if (registry.has(el)) return;
      var context = el.dataset.context || 'page';
      var inst    = new PolicyConsent(el, context);
      registry.set(el, inst);
      instances.push(inst);
    });
  }

  function unmount(el) {
    var inst = registry.get(el);
    if (!inst) return;
    inst.disconnect();
    registry.delete(el);
    instances = instances.filter(function (i) { return i !== inst; });
  }

  /* ── MutationObserver ─────────────────────────────────────────────────── */

  var domObserver = new MutationObserver(function (mutations) {
    var needsMount = false;

    mutations.forEach(function (m) {
      m.removedNodes.forEach(function (node) {
        if (!(node instanceof HTMLElement)) return;
        /* 只卸载真正从文档移除的节点（移动节点跳过） */
        if (document.contains(node)) return;

        if (node.classList && node.classList.contains('policy-consent')) {
          unmount(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('.policy-consent').forEach(function (child) {
            if (child instanceof HTMLElement && !document.contains(child)) {
              unmount(child);
            }
          });
        }
      });

      m.addedNodes.forEach(function (node) {
        if (!(node instanceof HTMLElement)) return;
        if (
          (node.classList && node.classList.contains('policy-consent')) ||
          (node.querySelectorAll && node.querySelectorAll('.policy-consent').length > 0)
        ) {
          needsMount = true;
        }
      });
    });

    if (needsMount) mountAll();
  });

  /* ── 启动 ─────────────────────────────────────────────────────────────── */

  registerDocListeners();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  domObserver.observe(document.body, { childList: true, subtree: true });

})();
